import {Component} from 'panel';

import {KEY_VALUES, propagateEvent} from '../../util/dom';
import {registerMPElement} from '../../util/register-element';
import {THEMES as BUTTON_THEMES} from '../../components/button';
import {
  ChangeEvent,
  FocusItemEvent,
  MenuChangeEvent,
  SearchFilterChangeEvent,
  Section,
  SelectDropdownState,
  SelectIcon,
  TooltipData,
} from './types';

import template from './index.jade';
import css from './index.styl?inline';

const LABEL_TRANSFORM = {
  NONE: ``,
  UPPERCASE: `uppercase`,
};

const THEME = {
  DEFAULT: ``,
  BARE: `bare`, // bare styling without padding, used with trigger slot
  BUTTON: `button`,
  BUILDER: `builder`,
  BUILDER_LIGHT: `builder-light`,
  LIGHT: `light`,
  SECONDARY: `secondary`,
  TERTIARY: `tertiary`,
};

const ICON_DEFAULT = {
  rightIcon: `caret-down`,
};

export enum MENU_ALIGNMENT {
  LEFT = `left`,
  RIGHT = `right`,
}

export default class SelectDropdown extends Component<SelectDropdownState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      options: {
        type: `json`,
        default: [],
        description: `interface: string[]`,
      },
      open: `boolean`,
      sections: {
        type: `json`,
        description:
          `interface: Section[]. ` +
          `Sections to pass to items-menu if options is not a simple list of strings. ` +
          `Can be passed via props too`,
      },
      'selected-option': {
        type: `string`,
        description: `Label of the selected option`,
      },
      'label-transform': {
        type: `string`,
        default: LABEL_TRANSFORM.NONE,
        enum: Object.values(LABEL_TRANSFORM),
        description: `Label transform to apply on selected label and options.`,
      },
      multiple: {
        type: `boolean`,
        description: `Allow multiple selection of items`,
      },
      'show-select-all': {
        type: `boolean`,
        description: `Provide control for selecting all/no items at once`,
      },
      'search-placeholder': {
        type: `string`,
        default: `Search`,
        description: `Text to show in an empty search box`,
      },
      searchable: {
        type: `boolean`,
        description: `If a search input box should be shown for searching`,
        default: true,
      },
      'search-threshold': {
        type: `number`,
        description: `If searchable=true, only show search input if number of items exceeds this threshold`,
        default: 10,
      },
      'header-title': {
        type: `string`,
        description: `header-title to show what kind of selection is being done. Useful for multilevel selection`,
      },
      'back-button': {
        type: `boolean`,
        description: `Whether to show back button. Only applicable if header is non empty string`,
      },
      'menu-alignment': {
        type: `string`,
        enum: Object.values(MENU_ALIGNMENT),
        description: `horizontal alignment of the menu, if not set defaults to automatic positioning based on the size of the window.`,
      },
      'menu-width': {
        type: `number`,
        description: `0 means same as button width, otherwise width in px`,
      },
      'menu-height': {
        type: `number`,
        default: 280,
        description: `height in px`,
      },
      icon: {
        type: `json`,
        default: null,
        description: `interface: SelectIcon`,
      },
      tooltip: {
        type: `json`,
      },
      theme: {
        type: `string`,
        default: THEME.DEFAULT,
        enum: Object.values(THEME),
        description: `Dictates styling`,
      },
      'button-theme': {
        type: `string`,
        enum: BUTTON_THEMES,
      },
      disabled: {
        type: `boolean`,
        description: `Whether or not mp-select should be disabled`,
      },
      'caret-pos': {
        type: `json`,
        description: `position of caret, if caret is shown e.g {left: '20px'}`,
        interface: `CaretPos`,
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        isOpen: false,
        isSearchable: false,
        searchFilter: ``,
        sections: null, // if passed in via a prop
        sectionFromInputFn: null,
        contextPanelTransform: null,
        tooltipData: null,
      },
      helpers: {
        THEME,

        handleClickItem: this.handleClickItem.bind(this),

        handleFocusItem: this.handleFocusItem.bind(this),

        handleSelectAll: (ev) => propagateEvent(this, ev),

        getItemsMenuSections: this.getItemsMenuSections.bind(this),

        getIcon: (): SelectIcon => {
          // only some themes have right caret-down icon by default
          const defaultIcon = [THEME.BUILDER, THEME.BUILDER_LIGHT, THEME.BARE].includes(this.attr(`theme`))
            ? {}
            : ICON_DEFAULT;
          return Object.assign({}, defaultIcon, this.attr(`icon`));
        },

        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },

        selectedLabel: (): string => {
          const selectedOption = this.attr(`selected-option`);
          if (selectedOption) {
            return selectedOption;
          }

          // if selected-option is not supplied
          // fall back to the isSelected item's label from sections
          const selectedItem = this.getItemsMenuSections()
            .map((section) => section.items.find((item) => item.isSelected))
            .find(Boolean);

          return selectedItem ? selectedItem.label : ``;
        },

        hasSelectedLabel: () => Boolean(this.helpers.selectedLabel()),

        getDropMenuHeight: () => this.attr(`menu-height`),

        getDropMenuWidth: (isSearchable: boolean) => {
          const menuWidth = this.attr(`menu-width`) ? this.attr(`menu-width`) : this.clientWidth;
          // if isSearchable, then have a minimum width so search input is still visible
          return isSearchable ? Math.max(menuWidth, 135) : menuWidth;
        },

        getDropMenuPosition: (menuWidth: number) => {
          const alignment = this.attr(`menu-alignment`);
          if (alignment) {
            return {[alignment]: 0};
          }

          // automatically figure out whether to right align or left align
          // based on whether dropdown will exceed window innerWidth
          const clientRect = this.getBoundingClientRect();
          if (clientRect.left + menuWidth >= window.innerWidth) {
            return {left: `${this.clientWidth - menuWidth}px`};
          }

          return {left: 0};
        },

        isSearchable: () => this.isSearchable(),

        focusSearchInput: (inputEl) => requestAnimationFrame(() => inputEl.focus()),

        handleSearchInput: (ev) => {
          const searchFilter = ev.target.value;
          this.update({searchFilter});
          this.dispatchEvent(
            new CustomEvent(`searchFilterChange`, {
              detail: {searchFilter},
            }) as SearchFilterChangeEvent,
          );
        },

        handleDropMenuChange: (ev) => ev.detail && ev.detail.state === `closed` && this.setDropMenuIsOpen(false),

        handleBackClick: () => {
          this.update({searchFilter: ``});
          this.dispatchEvent(new CustomEvent(`backClick`));
        },
      },
    };
  }

  /** @param val - pass sections via prop for perf reasons */
  set sections(val: Section[]) {
    this.update({sections: val});
  }

  /** @param value - a function that lazy computes sections to be shown, only when menu is opened */
  set sectionsFn(value: () => Section[]) {
    this._sectionsFn = value;
    this.updateSectionsFromSectionsFn();
  }

  /** @param value - function that returns a new section to be added depending on user input */
  set sectionFromInputFn(value: (input: string) => Section) {
    this.update({sectionFromInputFn: value});
  }

  /** @param value - function that returns context panel data to be shown for focused item */
  set contextPanelTransform(value: (item: any) => TooltipData) {
    this.update({contextPanelTransform: value});
  }
  private focusPrefix = ``;
  private focusPrefixClearTimeoutId = -1;

  private _sectionsFn: () => Section[] = null;

  connectedCallback() {
    super.connectedCallback();
    this.setFocusable(true);
    this.addEventListener(`focus`, this.handleFocus);
    this.addEventListener(`blur`, this.handleBlur);

    if (this.helpers.hasSlot(`trigger`)) {
      // user will want to style border/padding themselves if providing custom triggers
      this.setAttribute(`theme`, THEME.BARE);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(`focus`, this.handleFocus);
    this.removeEventListener(`blur`, this.handleBlur);
    this._sectionsFn = null;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `disabled`) {
      this.setFocusable(!this.attr(attr));
    } else if (attr === `open` && newVal) {
      this.setDropMenuIsOpen(this.attr(`open`));
    } else if (attr === `header-title`) {
      // clear searchFilter when header-title changes because header-title implies a different menu
      this.update({searchFilter: ``});
      this.updateSectionsFromSectionsFn();
    }
  }

  handleFocus() {
    this.addEventListener(`keydown`, this.handleKeydown);
  }

  handleBlur(ev) {
    this.removeEventListener(`keydown`, this.handleKeydown);
    const path = ev.path || (ev.composedPath && ev.composedPath());
    if (path && !path.includes(this)) {
      // don't close the dropdown if the click came from inside mp-select e.g from a footer slot
      this.setDropMenuIsOpen(false);
    }
  }

  isSearchable() {
    return (
      this.state.isSearchable ||
      (this.attr(`searchable`) &&
        (!this.attr(`search-threshold`) ||
          this.attr(`search-threshold`) <=
            this.getItemsMenuSections().reduce((acc, section) => acc + section.items.length, 0)))
    );
  }

  handleKeydown(ev) {
    const {ARROW_UP, ARROW_DOWN, ARROW_LEFT, ENTER, BACKSPACE} = KEY_VALUES;
    const {key} = ev;
    const {isOpen, searchFilter} = this.state;

    // arrow keys and enter open the dropdown (just like the html select)
    if (!isOpen && (key === ENTER || key === ARROW_UP || key === ARROW_DOWN)) {
      ev.stopPropagation();
      ev.preventDefault();
      this.setDropMenuIsOpen(true);
    } else if (key.match(/^\w$/g)) {
      // pressing an alphanumeric key focuses on the list item that begins with the user typed prefix (mnemonics)
      // if dropdown not open, it opens and focuses on that item ready for user to select it
      // the behaviour only applies if it's a non-searchable dropdown. it emulates by native html select behaviour
      if (!isOpen) {
        this.setDropMenuIsOpen(true);
      }
      if (this.isSearchable()) {
        this.update({searchFilter: key});
      } else {
        this.focusPrefix += key;
        // raf because we need for dropdown to open and render items-menu
        if (isOpen) {
          this.focusItemWithPrefix();
        } else {
          requestAnimationFrame(() => this.focusItemWithPrefix());
        }
      }
    } else if (isOpen && key === BACKSPACE) {
      this.focusPrefix = this.focusPrefix.slice(0, -1);
    } else if (isOpen && key === ARROW_LEFT && this.attr(`back-button`) && !searchFilter) {
      // same as doing a back button click
      this.helpers.handleBackClick();
    }
  }

  handleClickItem(ev) {
    const {item, modifierKeys} = ev.detail;

    this.dispatchEvent(
      new CustomEvent(`change`, {
        detail: {
          value: item.label,
          item,
          modifierKeys,
        },
      }) as ChangeEvent,
    );

    if (!this.attr(`multiple`) && !item.hasCaret) {
      this.setDropMenuIsOpen(false);
    }
  }

  handleFocusItem(ev) {
    const item = ev.detail.item;
    const contextPanelTransform = this.state.contextPanelTransform;

    if (contextPanelTransform) {
      this.update({tooltipData: contextPanelTransform(item)});
    }

    this.dispatchEvent(
      new CustomEvent(`focusItem`, {
        detail: {
          value: item.label,
          item,
        },
      }) as FocusItemEvent,
    );
  }

  /** @returns {Section[]} */
  getItemsMenuSections() {
    if (this.attr(`sections`)) {
      return this.attr(`sections`);
    } else if (this.state.sections) {
      return this.state.sections;
    }

    // otherwise create sections format from options and selected option
    const options = this.attr(`options`);
    const selectedLabel = this.attr(`selected-option`);
    return [
      {
        items: options.map((option) => ({
          label: option,
          labelTheme: this.attr(`label-transform`) === LABEL_TRANSFORM.UPPERCASE ? `strong` : null,
          isSelected: option === selectedLabel,
        })),
      },
    ];
  }

  updateSectionsFromSectionsFn() {
    if (typeof this._sectionsFn === `function`) {
      this.update({sections: this.state.isOpen ? this._sectionsFn() : null});
    }
  }

  setDropMenuIsOpen(isOpen) {
    if (this.state.isOpen !== isOpen) {
      this.update({isOpen, searchFilter: ``, isSearchable: false});
      this.updateSectionsFromSectionsFn();
      this.dispatchEvent(new CustomEvent(`menuChange`, {detail: {isOpen}}) as MenuChangeEvent);

      if (isOpen && this.attr(`selected-option`) && !this.helpers.hasSlot(`body`)) {
        // Wait for the dropdown to render before we try to focus the item
        window.requestAnimationFrame(() => this.focusItemWithLabel(this.attr(`selected-option`)));
      }

      isOpen ? this.classList.add(`is-open`) : this.classList.remove(`is-open`);
    }
  }

  focusItemWithPrefix() {
    const itemsMenuEl: any = this.el.querySelector(`mp-items-menu`);
    itemsMenuEl.focusItemWithPrefix(this.focusPrefix);

    // clear focusPrefix after one second of inactive typing
    if (this.focusPrefixClearTimeoutId) {
      clearTimeout(this.focusPrefixClearTimeoutId);
    }

    this.focusPrefixClearTimeoutId = window.setTimeout(() => {
      this.focusPrefix = ``;
      this.focusPrefixClearTimeoutId = -1;
    }, 1000);
  }

  focusItemWithLabel(label: string) {
    const itemsMenuEl: any = this.el.querySelector(`mp-items-menu`);
    itemsMenuEl.focusItemWithLabel(label);
  }

  setFocusable(isFocusable: boolean) {
    // add tabindex=0 as a host attribute so it can be focusable via tab and shift+tab
    // just the way a normal html select is focusable by default
    if (isFocusable) {
      this.setAttribute(`tabindex`, `0`);
    } else {
      this.removeAttribute(`tabindex`);
    }
  }
}

registerMPElement(`mp-select`, SelectDropdown);
